/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Skeleton as MUISkeleton, SkeletonProps as MUISkeletonProps} from '@material-ui/lab';
import styled, {keyframes} from 'styled-components';
import {SkeletonTextSizeEnum} from '@/types/types';

export interface SkeletonProps extends MUISkeletonProps {
  size?: keyof typeof SkeletonTextSizeEnum;
}

const widthAnimationProps = '1s ease-out';
const fadeAnimationProps = '2s ease-out';

const widthKeyframe = ({width}) => keyframes`
  from {
    width : 0px;
  }
  to {
    width: ${width ? `${width}px` : '100%'}
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const StyledSkeletonContainer = styled.div`
    animation: ${fadeIn} ${fadeAnimationProps};
`;
const StyledMUISkeleton = styled(MUISkeleton)`
    animation: ${props => widthKeyframe(props)} ${widthAnimationProps}, ${fadeIn} ${fadeAnimationProps};
`;

/**
 *
 * @Skeleton
 *
 * Implement tombstone loading components per DLS
 *
 * https://www.figma.com/proto/60MIFpfbKhV6fOqrrKRRWN/04_Hero-Screen-Motion?node-id=1%3A108&
 *  viewport=2463%2C692%2C0.10229705274105072&scaling=min-zoom
 *
 * ```typescript
 *
 * import React from 'react';
 * import {Skeleton} from '@ion/dls/src/components/skeleton/Skeleton';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <Skeleton variant='text' />
 *       <Skeleton variant='text' size='small' />
 *       <Skeleton variant='text' width={500} />
 *       <Skeleton variant='circle' />
 *       <Skeleton variant='rect' width={250} height={100} />
 *       <Skeleton variant='rect' animation='wave' width={250} height={100} />
 *       <Skeleton variant='rect' animation='pulse' width={250} height={150} />
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
export const Skeleton = ({
  size = 'medium',
  ...props
}: SkeletonProps) => {
  const textSize = size === 'small' ? 50 : 75;

  function getSize() {
    if (props.variant === 'circle') {
      return {
        width  : textSize,
        height : textSize
      };
    }
  }

  return (
    <>
      {(!props.variant || props.variant === 'text') ? <StyledMUISkeleton {...(textSize && {height: textSize})} {...props} animation={false} /> : (
        <StyledSkeletonContainer>
          <MUISkeleton {...(getSize())} animation={false} {...props} />
        </StyledSkeletonContainer>)}
    </>
  );
};
